// src/services/api.js
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
console.log('API_URL:', API_URL);

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
  console.log('Auth headers:', { hasToken: !!token, tokenLength: token?.length });
  return headers;
};

// Authentication API
export const authAPI = {
  login: async (username, password) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!response.ok) throw new Error('Login failed');
    return response.json();
  }
};

// Admin API
export const adminAPI = {
  getStudents: async (schoolId, batchStatus = null) => {
    console.log('Fetching students for schoolId:', schoolId, 'batchStatus:', batchStatus);

    // Try the most likely endpoint first - GET with query parameter
    try {
      console.log('Trying: GET /admin/students with query parameter');
      let url = `${API_URL}/admin/students?schoolId=${schoolId}`;
      if (batchStatus) {
        url += `&batchStatus=${batchStatus}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      console.log('Response status:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('Students data:', data);
        return data;
      } else {
        const errorText = await response.text();
        console.log('GET failed:', errorText);
      }
    } catch (err) {
      console.log('GET error:', err.message);
    }

    // Try POST with schoolId in body
    try {
      console.log('Trying: POST /admin/students/detailed with body');
      const response = await fetch(`${API_URL}/admin/students/detailed`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ schoolId })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Students detailed data:', data);
        return data;
      } else {
        const errorText = await response.text();
        console.log('POST detailed failed:', errorText);
      }
    } catch (err) {
      console.log('POST detailed error:', err.message);
    }

    throw new Error('Failed to fetch students - check console for details');
  },

  createStudent: async (studentData) => {
    console.log('API: Creating student with payload:', studentData);

    // Always use FormData since backend expects @RequestParam (form data)
    const formData = new FormData();
    let headers = getAuthHeaders();

    // Add all text fields (skip null, undefined, and empty string values for optional fields)
    Object.keys(studentData).forEach(key => {
      const value = studentData[key];
      const isFileField = ['bloodGroupProof', 'addressProof', 'idProof', 'graduationProof', 'oldLicenseProof'].includes(key);
      const isOptionalDateField = ['dateOfBirth', 'llrAppliedDate', 'llrObtainedDate'].includes(key);

      // Skip file fields, null values, undefined values, and empty strings for optional date fields
      if (!isFileField && value !== null && value !== undefined) {
        if (isOptionalDateField && value === '') {
          // Don't append empty date strings
          return;
        }
        formData.append(key, value);
      }
    });

    // Add file fields if present
    if (studentData.bloodGroupProof) formData.append('bloodGroupProof', studentData.bloodGroupProof);
    if (studentData.addressProof) formData.append('addressProof', studentData.addressProof);
    if (studentData.idProof) formData.append('idProof', studentData.idProof);
    if (studentData.graduationProof) formData.append('graduationProof', studentData.graduationProof);
    if (studentData.oldLicenseProof) formData.append('oldLicenseProof', studentData.oldLicenseProof);

    // Remove Content-Type header to let browser set it with boundary
    delete headers['Content-Type'];

    console.log('API: Sending FormData to backend');

    const response = await fetch(`${API_URL}/admin/students`, {
      method: 'POST',
      headers: headers,
      body: formData
    });

    console.log('API: Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.log('API: Error response:', errorText);
      throw new Error('Failed to create student: ' + errorText);
    }

    const result = await response.json();
    console.log('API: Success response:', result);
    return result;
  },

  updateStudent: async (id, studentData) => {
    console.log('API: Updating student with payload:', studentData);

    // Always use FormData for updates since backend expects @RequestParam
    const formData = new FormData();
    let headers = getAuthHeaders();

    // Add all text fields (skip null, undefined, and empty string values for optional fields)
    Object.keys(studentData).forEach(key => {
      const value = studentData[key];
      const isFileField = ['bloodGroupProof', 'addressProof', 'idProof', 'graduationProof', 'oldLicenseProof'].includes(key);
      const isOptionalDateField = ['dateOfBirth', 'llrAppliedDate', 'llrObtainedDate'].includes(key);

      // Skip file fields, null values, undefined values, and empty strings for optional date fields
      if (!isFileField && value !== null && value !== undefined) {
        if (isOptionalDateField && value === '') {
          // Don't append empty date strings
          return;
        }
        formData.append(key, value);
      }
    });

    // Add file fields if present
    if (studentData.bloodGroupProof) formData.append('bloodGroupProof', studentData.bloodGroupProof);
    if (studentData.addressProof) formData.append('addressProof', studentData.addressProof);
    if (studentData.idProof) formData.append('idProof', studentData.idProof);
    if (studentData.graduationProof) formData.append('graduationProof', studentData.graduationProof);
    if (studentData.oldLicenseProof) formData.append('oldLicenseProof', studentData.oldLicenseProof);

    // Remove Content-Type header to let browser set it with boundary
    delete headers['Content-Type'];

    console.log('API: Sending FormData for update');

    const response = await fetch(`${API_URL}/admin/students/${id}`, {
      method: 'PUT',
      headers: headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.log('API: Error response:', errorText);
      throw new Error('Failed to update student: ' + errorText);
    }

    return response.json();
  },

  deleteStudent: async (id) => {
    const response = await fetch(`${API_URL}/admin/students/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete student');
    return response.json();
  },

  getBatches: async (schoolId, status = null) => {
    try {
      console.log('Fetching batches with GET query parameter');
      let url = `${API_URL}/admin/batches?schoolId=${schoolId}`;
      if (status) {
        url += `&status=${status}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Batches data:', data);
        return data;
      } else {
        const errorText = await response.text();
        console.log('Batches GET failed:', errorText);
      }
    } catch (err) {
      console.log('Batches error:', err.message);
    }

    return []; // Return empty array as fallback
  },

  closeBatch: async (batchId) => {
    const response = await fetch(`${API_URL}/admin/batches/${batchId}/close`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to close batch');
    }
    return response.json();
  },

  reopenBatch: async (batchId) => {
    const response = await fetch(`${API_URL}/admin/batches/${batchId}/reopen`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to reopen batch');
    }
    return response.json();
  },

  createBatch: async (batchData) => {
    const response = await fetch(`${API_URL}/admin/batches`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(batchData)
    });
    if (!response.ok) throw new Error('Failed to create batch');
    return response.json();
  },

  getTrainers: async (schoolId) => {
    try {
      console.log('Fetching trainers with GET query parameter');
      const response = await fetch(`${API_URL}/admin/trainers?schoolId=${schoolId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Trainers data:', data);
        return data;
      } else {
        const errorText = await response.text();
        console.log('Trainers GET failed:', errorText);
      }
    } catch (err) {
      console.log('Trainers error:', err.message);
    }

    return []; // Return empty array as fallback
  },

  createTrainer: async (trainerData) => {
    const response = await fetch(`${API_URL}/admin/trainers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(trainerData)
    });
    if (!response.ok) throw new Error('Failed to create trainer');
    return response.json();
  },

  updateBatch: async (id, batchData) => {
    const response = await fetch(`${API_URL}/admin/batches/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(batchData)
    });
    if (!response.ok) throw new Error('Failed to update batch');
    return response.json();
  },

  updateTrainer: async (id, trainerData) => {
    const response = await fetch(`${API_URL}/admin/trainers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(trainerData)
    });
    if (!response.ok) throw new Error('Failed to update trainer');
    return response.json();
  },

  // Payment operations
  getStudentPayments: async (studentId) => {
    const response = await fetch(`${API_URL}/admin/students/${studentId}/payments`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch payments');
    return response.json();
  },

  addPayment: async (studentId, paymentData) => {
    console.log('Adding payment for student:', studentId);
    console.log('Payment data:', paymentData);

    const response = await fetch(`${API_URL}/admin/students/${studentId}/payments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(paymentData)
    });

    console.log('Payment response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Payment error response:', errorText);
      throw new Error('Failed to add payment: ' + errorText);
    }
    return response.json();
  },

  deletePayment: async (paymentId) => {
    const response = await fetch(`${API_URL}/admin/payments/${paymentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete payment');
    return response.json();
  },

  downloadPaymentHistory: async (studentId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/admin/students/${studentId}/download-payment-history`, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download payment history');
    }

    return response.blob();
  }
};

// Trainer API
export const trainerAPI = {
  markAttendance: async (attendanceData) => {
    const response = await fetch(`${API_URL}/trainer/attendance`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(attendanceData)
    });
    if (!response.ok) throw new Error('Failed to mark attendance');
    return response.json();
  },

  getBatchesByTrainer: async (trainerId) => {
    const response = await fetch(`${API_URL}/trainer/batches/${trainerId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch batches');
    return response.json();
  },

  getStudentsByBatch: async (batchId) => {
    const response = await fetch(`${API_URL}/trainer/students/${batchId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch students');
    return response.json();
  },

  getStudentAttendance: async (studentId) => {
    const response = await fetch(`${API_URL}/trainer/attendance/${studentId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch attendance');
    return response.json();
  }
};

// Student API
export const studentAPI = {
  getProfile: async (userId) => {
    const response = await fetch(`${API_URL}/student/profile/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch profile');
    return response.json();
  },

  updateProfile: async (userId, profileData) => {
    const response = await fetch(`${API_URL}/student/profile/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(profileData)
    });
    if (!response.ok) throw new Error('Failed to update profile');
    return response.json();
  },

  getAttendance: async (userId) => {
    const response = await fetch(`${API_URL}/student/attendance/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch attendance');
    return response.json();
  },

  getPayments: async (userId) => {
    const response = await fetch(`${API_URL}/student/payments/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch payments');
    return response.json();
  },

  uploadProfilePicture: async (userId, file) => {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/student/upload-profile-picture/${userId}`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
        // Don't set Content-Type - browser will set it with boundary
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload profile picture');
    }

    return response.json();
  }
};

// Document API (accessible by both ADMIN and STUDENT)
export const documentAPI = {
  uploadDocument: async (studentId, documentType, file) => {
    const formData = new FormData();
    formData.append('studentId', studentId);
    formData.append('documentType', documentType);
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/documents/upload`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
        // Don't set Content-Type - browser will set it with boundary
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to upload document');
    }

    return response.json();
  },

  getDocument: async (documentId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/documents/${documentId}`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch document');
    }

    return response.blob(); // Returns file as blob
  },

  getDocumentByType: async (studentId, documentType) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/documents/student/${studentId}/type/${documentType}`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Document not found
      }
      throw new Error('Failed to fetch document');
    }

    return response.blob();
  },

  getStudentDocuments: async (studentId) => {
    const response = await fetch(`${API_URL}/documents/student/${studentId}`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch student documents');
    }

    return response.json(); // Returns metadata array
  },

  getAllDocuments: async () => {
    const response = await fetch(`${API_URL}/documents`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch all documents');
    }

    return response.json();
  },

  deleteDocument: async (documentId) => {
    const response = await fetch(`${API_URL}/documents/${documentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to delete document');
    }

    return response.json();
  },

  checkDocumentExists: async (studentId, documentType) => {
    const response = await fetch(`${API_URL}/documents/exists?studentId=${studentId}&documentType=${documentType}`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to check document existence');
    }

    return response.json(); // Returns { exists: boolean }
  }
};

export default {
  authAPI,
  adminAPI,
  trainerAPI,
  studentAPI,
  documentAPI
};